import React, { useState } from 'react';
import { RefreshCw, CheckCircle, XCircle, AlertCircle } from 'lucide-react';
import { showNotification, showError } from '../../utils/notificationHandler';

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || '';

const OrderSyncButton = () => {
  const [syncing, setSyncing] = useState(false);
  const [progress, setProgress] = useState(0);
  const [currentTask, setCurrentTask] = useState(null);
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);

  const handleSync = async () => {
    setSyncing(true);
    setProgress(0);
    setResult(null);
    setError(null);

    try {
      // Trigger async sync job via FastAPI (using relative URL through nginx proxy)
      const response = await fetch(`${API_BASE_URL}/api/v1/sync/portal-orders/async`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        }
      });

      if (!response.ok) {
        throw new Error(`Failed to trigger sync: ${response.statusText}`);
      }

      const data = await response.json();
      const taskId = data.task_id;
      
      setCurrentTask(taskId);
      
      // Poll for progress
      pollTaskStatus(taskId);
      
    } catch (err) {
      setSyncing(false);
      setError(err.message);
      showError(err, 'Failed to start sync');
      console.error('Error triggering order sync:', err);
      console.error('Detailed error:', err);
    }
  };

  const pollTaskStatus = async (taskId) => {
    const pollInterval = 10000; // Poll every 10 seconds (reduced from 2 seconds)
    
    const poll = async () => {
      try {
        const response = await fetch(`${API_BASE_URL}/api/v1/sync/tasks/${taskId}`, {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
          }
        });

        if (!response.ok) {
          throw new Error(`Failed to get task status: ${response.statusText}`);
        }

        const data = await response.json();
        const status = data.status;
        const progressData = data.progress || {};

        // Update progress
        if (progressData.percentage) {
          setProgress(progressData.percentage);
        }

        // Handle different task states
        if (status === 'pending' || status === 'running') {
          // Continue polling - store timeout ID globally for cleanup
          window.pollTimeoutId = setTimeout(poll, pollInterval);
        } else if (status === 'completed') {
          // Task completed successfully - stop polling
          if (window.pollTimeoutId) {
            clearTimeout(window.pollTimeoutId);
            window.pollTimeoutId = null;
          }
          setSyncing(false);
          setResult(data.result);
          setProgress(100);
          
          const ordersUpdated = data.result?.orders_synced || 0;
          const ordersFailed = data.result?.orders_failed || 0;
          
          if (ordersFailed > 0) {
            showNotification(
              `Sync completed with warnings: ${ordersUpdated} orders updated, ${ordersFailed} failed`,
              'warning'
            );
          } else {
            showNotification(
              `Sync completed successfully: ${ordersUpdated} orders updated`,
              'success'
            );
          }
        } else if (status === 'failed') {
          // Task failed - stop polling
          if (window.pollTimeoutId) {
            clearTimeout(window.pollTimeoutId);
            window.pollTimeoutId = null;
          }
          setSyncing(false);
          setError(data.error || 'Sync failed');
          showError(data.error || 'Sync failed', 'Sync failed');
        }
      } catch (err) {
        // Error occurred - stop polling
        if (window.pollTimeoutId) {
          clearTimeout(window.pollTimeoutId);
          window.pollTimeoutId = null;
        }
        setSyncing(false);
        setError(err.message);
        showError(err, 'Failed to get sync status');
      }
    };

    // Start polling
    poll();
  };

  const handleCancel = async () => {
    if (currentTask) {
      try {
        // Note: FastAPI doesn't support task cancellation yet
        // Just stop polling and reset state
        // Clear any pending polling timeout
        if (window.pollTimeoutId) {
          clearTimeout(window.pollTimeoutId);
          window.pollTimeoutId = null;
        }
        setSyncing(false);
        setProgress(0);
        setCurrentTask(null);
        showNotification('Sync monitoring stopped (task continues in background)', 'info');
      } catch (err) {
        showError(err, 'Failed to stop sync monitoring');
      }
    }
  };

  return (
    <div className="bg-white p-6 rounded-lg shadow">
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-lg font-semibold text-gray-900">Order Sync</h3>
        {syncing && (
          <span className="text-sm text-gray-500">
            Task ID: {currentTask?.substring(0, 8)}...
          </span>
        )}
      </div>

      {/* Sync Button */}
      <div className="mb-4">
        <button
          onClick={handleSync}
          disabled={syncing}
          className={`
            inline-flex items-center px-4 py-2 rounded-md font-medium
            transition-colors duration-200
            ${syncing
              ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
              : 'bg-brand hover:bg-brand-hover text-black'
            }
          `}
        >
          <RefreshCw className={`w-4 h-4 mr-2 ${syncing ? 'animate-spin' : ''}`} />
          {syncing ? 'Syncing...' : 'Sync Orders'}
        </button>

        {syncing && (
          <button
            onClick={handleCancel}
            className="ml-2 inline-flex items-center px-4 py-2 rounded-md font-medium bg-red-500 hover:bg-red-600 text-white transition-colors duration-200"
          >
            <XCircle className="w-4 h-4 mr-2" />
            Cancel
          </button>
        )}
      </div>

      {/* Progress Bar */}
      {syncing && (
        <div className="mb-4">
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm font-medium text-gray-700">
              Progress
            </span>
            <span className="text-sm font-medium text-gray-700">
              {progress}%
            </span>
          </div>
          <div className="w-full bg-gray-200 rounded-full h-2.5">
            <div
              className="bg-brand h-2.5 rounded-full transition-all duration-300"
              style={{ width: `${progress}%` }}
            />
          </div>
          <p className="text-xs text-gray-500 mt-2">
            Syncing orders with ERP system...
          </p>
        </div>
      )}

      {/* Success Result */}
      {result && !syncing && (
        <div className="p-4 bg-green-50 border border-green-200 rounded-md">
          <div className="flex items-start">
            <CheckCircle className="w-5 h-5 text-green-600 mt-0.5 mr-3 flex-shrink-0" />
            <div className="flex-1">
              <h4 className="text-sm font-medium text-green-800 mb-2">
                Sync Completed Successfully
              </h4>
              <div className="text-sm text-green-700 space-y-1">
                <p>Orders checked: <span className="font-semibold">{result.orders_checked || 0}</span></p>
                <p>Orders synced: <span className="font-semibold">{result.orders_synced || 0}</span></p>
                <p>Orders updated: <span className="font-semibold">{result.orders_with_changes || 0}</span></p>
                {result.orders_failed > 0 && (
                  <p className="text-red-600">
                    Orders failed: <span className="font-semibold">{result.orders_failed}</span>
                  </p>
                )}
                <p className="text-xs text-green-600 mt-2">
                  Duration: {((result.duration_ms || 0) / 1000).toFixed(1)}s
                </p>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Error Result */}
      {error && !syncing && (
        <div className="p-4 bg-red-50 border border-red-200 rounded-md">
          <div className="flex items-start">
            <AlertCircle className="w-5 h-5 text-red-600 mt-0.5 mr-3 flex-shrink-0" />
            <div className="flex-1">
              <h4 className="text-sm font-medium text-red-800 mb-2">
                Sync Failed
              </h4>
              <p className="text-sm text-red-700">{error}</p>
            </div>
          </div>
        </div>
      )}

      {/* Info Text */}
      {!syncing && !result && !error && (
        <div className="text-sm text-gray-600">
          <p>Click "Sync Orders" to synchronize portal orders with the ERP system.</p>
          <p className="mt-1 text-xs text-gray-500">
            This will update order statuses, invoice numbers, and fulfillment information.
          </p>
        </div>
      )}
    </div>
  );
};

export default OrderSyncButton;
